import { Controller } from "@hotwired/stimulus"
import { setClass } from "../lib/helpers"

// Flash Bar controller that receives flash messages and displays them at the top of the page.
// It supports two types of messages: "notice" and "alert". The bar slides down from the top
// of the page and displays the message with a progress bar that fills up over time.
//
// Use data-turbo-permanent to ensure the flash bar persists across page loads and maintains its
// state.
//
// Render an HTML element with a FlashDispatcher controller to dispatch flash messages to this
// controller. This would typically be extracted from the flash messages in the Rails view.
//
// Import the flash function from this module to dispatch flash messages from anywhere in your
// JavaScript code.
//
export default class extends Controller {
  static values = { type: String, message: String, progress: Number }
  static targets = [ "message", "progress" ]
  static SLIDE_DURATION = 333 // match the css animation
  static FRAME_TIME = 16 // millisecond per frame
  static containerClasses = [
    "absolute",
    "top-0",
    "right-0",
    "left-0",
    "h-12",
    "z-10",
    "transition-transform",
    "duration-[333ms]",
    "ease-in-out",
    "flex",
    "flex-col",
    "text-center",
  ]
  static containerNoticeClasses = [
    "border-t",
    "border-t-azure-700",
    "dark:border-t-azure-900",
    "bg-azure-700",
    "dark:bg-azure-900",
  ]
  static containerAlertClasses = [
    "border-t",
    "border-t-red-700",
    "dark:border-t-azure-900",
    "bg-red-700",
    "dark:bg-red-900",
  ]
  static invisibleClasses = [
    "-translate-y-12",
  ]
  static visibleClasses = [
    "translate-y-0",
  ]
  static messageClasses = [
    "flex",
    "flex-1",
    "justify-center",
    "items-center",
    "text-white",
    "transition-[background-color]",
    "duration-[333ms]",
  ]
  static messageNoticeClasses = [
    "bg-azure-600",
    "dark:bg-azure-700",
  ]
  static messageAlertClasses = [
    "bg-red-600",
    "dark:bg-red-900",
  ]
  static progressClasses = [
    "h-0.5",
  ]
  static progressNoticeClasses = [
    "bg-azure-50",
    "dark:bg-azure-400",
  ]
  static progressAlertClasses = [
    "bg-red-50",
    "dark:bg-red-400",
  ]

  connect() {
    this.incrementProgress = this.incrementProgress.bind(this)
    this.adjustPosition = this.adjustPosition.bind(this)
    this.element.addEventListener("flash:message", this.flashMessageEvent)
    document.addEventListener("scroll", this.adjustPosition)
  }

  disconnect() {
    document.removeEventListener("scroll", this.adjustPosition)
  }

  flashMessageEvent = (event) => {
    this.typeValue = event.detail.flash.type
    this.messageValue = event.detail.flash.message
    this.start()
  }

  start() {
    this.hide()

    setTimeout(() => {
      this.render()
      this.show()
      this.trackProgress()
      this.adjustPosition()
    }, 0)
  }

  stop() {
    this.hide()
    this.reset()
  }

  render() {
    switch (this.typeValue) {
    case "notice":
      setClass(
        this.element,
        this.constructor.invisibleClasses,
        this.constructor.containerClasses,
        this.constructor.containerNoticeClasses,
      )
      setClass(
        this.messageTarget,
        this.constructor.messageClasses,
        this.constructor.messageNoticeClasses
      )
      setClass(
        this.progressTarget,
        this.constructor.progressClasses,
        this.constructor.progressNoticeClasses
      )
      break
    case "alert":
      setClass(
        this.element,
        this.constructor.containerClasses,
        this.constructor.containerAlertClasses,
        this.constructor.invisibleClasses
      )
      setClass(
        this.messageTarget,
        this.constructor.messageClasses,
        this.constructor.messageAlertClasses
      )
      setClass(
        this.progressTarget,
        this.constructor.progressClasses,
        this.constructor.progressAlertClasses
      )
      break
    }

    this.messageTarget.innerHTML = this.messageValue
  }

  reset() {
    this.messageValue = ""
    this.typeValue = ""
    this.progressValue = 0
  }

  show() {
    this.element.classList.remove(...this.constructor.invisibleClasses)
    this.element.classList.add(...this.constructor.visibleClasses)
  }

  hide () {
    this.element.classList.remove(...this.constructor.visibleClasses)
    this.element.classList.add(...this.constructor.invisibleClasses)
  }

  trackProgress() {
    if (!this.typeValue || !this.messageValue)
      return

    if (this.progressValue) {
      this.progressValue = 0
    } else {
      this.incrementProgress()
    }
  }

  incrementProgress() {
    if (this.progressValue >= 100) {
      this.progressTarget.style.width = `100%`
      this.stop()
    } else {
      this.progressTarget.style.width = `${this.progressValue}%`
      this.progressValue += 0.425
      setTimeout(this.incrementProgress, this.constructor.FRAME_TIME)
    }
  }

  adjustPosition(e) {
    if (window.scrollY >= 80) {
      this.element.classList.add("fixed")
      this.element.classList.remove("absolute")
    } else {
      this.element.classList.add("absolute")
      this.element.classList.remove("fixed")
    }
  }
}

export function flash(type, ...message) {
  const event = new CustomEvent(
    "flash:message",
    {
      detail: {
        flash: {
          type: type,
          message: message.join(" ")
        }
      }
    }
  )

  dispatch(event)
}

function dispatch(event) {
  document.getElementById("flash")?.dispatchEvent(event)
}
